import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";

import { toast } from "@outpave/ds";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAccountValidationSchema,
  getUpdateAccountValidationSchema,
} from "validations/account";
import Role from "enums/Role";
import StatusType from "enums/StatusType";
import { ADD_NEW_COMPANY, GET_COMPANY, UPDATE_COMPANY } from "graphql/company";
import CardHolderType from "enums/CardHolderType";

export type FormikAddAccount = {
  user: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    status: string;
    address: {
      addressName: string;
      address1: string;
      address2: string;
      city: string;
      state: string;
      zipcode: string;
      country: string;
    };
    role: string;
  };
  account: {
    businessLegalName: string;
    description: string;
    businessDBA: string;
    businessCardName: string;
    businessEIN: string;
    businessStructure: string;
    businessEmail: string;
    phoneNumber: string;
    websiteURL: string;
    [key: string]: string | boolean | null | { id: string; name: string };
  };
  appPlanIds: string[];
  addOnPlanIds: string[];
  /** Only sent when the platform offers more than one issuer. */
  platformIntegrationId?: string;
  /** Only sent when the issuer's card programs offer both cardholder types. */
  cardHolderType?: CardHolderType;
};

type UseAccountDataParams = {
  handleClose: (refresh: boolean, newAccountId?: string) => void;
  accountId?: string;
  refreshAccounts?: () => void;
};

const useAccountData = ({
  handleClose,
  accountId = "",
  refreshAccounts = () => {},
}: UseAccountDataParams) => {
  const { t } = useTranslation();

  const [validationSchema, setValidationSchema] = useState<any>(null);

  const companyFields = [
    "businessLegalName",
    "businessDBA",
    "businessEIN",
    "businessStructure",
    "businessCardName",
    "description",
    "businessEmail",
    "phoneNumber",
    "websiteURL",
    "status",
  ];

  const formikAddAccount = useFormik<FormikAddAccount>({
    initialValues: {
      user: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        status: StatusType.PENDING,
        address: {
          addressName: "",
          address1: "",
          address2: "",
          city: "",
          state: "",
          zipcode: "",
          country: "US",
        },
        role: Role.Admin,
      },
      account: {
        businessLegalName: "",
        description: "",
        businessDBA: "",
        businessCardName: "",
        businessEIN: "",
        businessEmail: "",
        businessStructure: "",
        phoneNumber: "",
        websiteURL: "",
      },
      appPlanIds: [],
      addOnPlanIds: [],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (accountId) {
        // Only send fields that have changed
        const updatedCompanyFields = companyFields.reduce(
          (acc: { [key: string]: any }, key) => {
            if (values.account[key] !== companySettingsData.company[key]) {
              acc[key] = values.account[key];
            }
            return acc;
          },
          {},
        );
        if (Object.keys(updatedCompanyFields).length > 0) {
          await updateCompany({
            variables: { id: accountId, input: updatedCompanyFields },
          });
        } else {
          handleClose(true);
        }
      } else {
        addNewCompany({
          variables: {
            input: {
              ...values.user,
              accountName: values.account.businessLegalName,
              appPlanIds: values.appPlanIds,
              addOnPlanIds: values.addOnPlanIds,
              ...(values.platformIntegrationId && {
                platformIntegrationId: values.platformIntegrationId,
              }),
              ...(values.cardHolderType && {
                cardHolderType: values.cardHolderType,
              }),
            },
          },
        });
      }
    },
  });

  const [addNewCompany, { loading: isAddingCompany }] = useMutation(
    ADD_NEW_COMPANY,
    {
      onCompleted: (data) => {
        toast.success(t("platform.addNewAccountSuccess"));
        handleClose(true);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );

  const [updateCompany, { loading: isUpdatingCompany }] = useMutation(
    UPDATE_COMPANY,
    {
      // Refetched only on success: a refetch resets the form, which would drop edits a failed save kept.
      onCompleted: () => {
        toast.success(t("platform.updateAccountSuccess"));
        refetchCompanySettings();
        refreshAccounts();
        handleClose(true);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );

  const {
    data: companySettingsData,
    refetch: refetchCompanySettings,
    loading: loadingCompanySettings,
  } = useQuery(GET_COMPANY, {
    variables: { id: accountId },
    skip: Boolean(!accountId),
    fetchPolicy: "network-only",
  });

  const loadedCardName = companySettingsData?.company?.businessCardName;

  useEffect(() => {
    const newValidationSchema =
      accountId && accountId !== ""
        ? getUpdateAccountValidationSchema(loadedCardName)
        : createAccountValidationSchema;
    setValidationSchema(newValidationSchema);
    formikAddAccount.setFormikState((prevState) => ({
      ...prevState,
      validationSchema: newValidationSchema,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, loadedCardName]);

  const isLoading = useMemo(() => {
    return isAddingCompany || isUpdatingCompany || loadingCompanySettings;
  }, [isAddingCompany, isUpdatingCompany, loadingCompanySettings]);

  // Saves only, without the settings fetch: the account drawers gate their
  // close paths on this, and a background refetch must not trap the user.
  // `formik.isSubmitting` can't stand in — the create path doesn't await.
  const isSavingAccount = isAddingCompany || isUpdatingCompany;

  const updateFormikValues = (data: any, fields: string[]) => {
    return fields.reduce(
      (acc: Record<string, string>, field) => {
        acc[field] = data[field] || "";
        return acc;
      },
      {} as Record<string, string>,
    );
  };

  useEffect(() => {
    if (companySettingsData?.company?.id) {
      const updatedCompanySettings = updateFormikValues(
        companySettingsData?.company || {},
        companyFields,
      );

      formikAddAccount.resetForm({
        values: {
          ...formikAddAccount.values,
          account: {
            ...formikAddAccount.values.account,
            ...updatedCompanySettings,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySettingsData]);

  return {
    formikAddAccount,
    company:
      companySettingsData?.company?.id === accountId
        ? companySettingsData.company
        : undefined,
    refetchCompany: refetchCompanySettings,
    isLoading,
    isSavingAccount,
  };
};

export default useAccountData;
